import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowRight, Loader2 } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LEGAL_LINKS = [
  { slug: "terms-and-conditions", label: "Terms & Conditions", href: "/terms-and-conditions" },
  { slug: "privacy-policy", label: "Privacy Policy", href: "/privacy-policy" },
  { slug: "refund-cancellation-policy", label: "Refund & Cancellation Policy", href: "/refund-policy" },
  { slug: "shipping-policy", label: "Shipping Policy", href: "/shipping-policy" },
];

const MD_COMPONENTS = {
  h2: ({ children }) => (
    <h2 className="serif mt-10 first:mt-0 text-[22px] sm:text-[26px] leading-[1.25] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>{children}</h2>
  ),
  h3: ({ children }) => <h3 className="serif mt-7 text-[19px] text-[var(--hk-ink)]">{children}</h3>,
  p: ({ children }) => <p className="mt-4 text-[15px] leading-[1.9] text-[var(--hk-ink-2)]">{children}</p>,
  ul: ({ children }) => <ul className="mt-3 pl-5 list-disc space-y-1.5 text-[15px] leading-[1.8] text-[var(--hk-ink-2)]">{children}</ul>,
  ol: ({ children }) => <ol className="mt-3 pl-5 list-decimal space-y-1.5 text-[15px] leading-[1.8] text-[var(--hk-ink-2)]">{children}</ol>,
  strong: ({ children }) => <strong className="text-[var(--hk-ink)] font-semibold">{children}</strong>,
  a: ({ href, children }) => <a href={href} className="text-[var(--hk-orange-deep)] underline underline-offset-2">{children}</a>,
  blockquote: ({ children }) => (
    <blockquote
      className="mt-6 rounded-2xl border p-5 md:p-6 [&>p]:mt-0 [&>p]:text-[14.5px]"
      style={{ borderColor: "rgba(184, 94, 36, 0.35)", background: "linear-gradient(120deg, rgba(217,117,53,0.06), rgba(201,169,97,0.06))" }}
    >
      {children}
    </blockquote>
  ),
};

/** Generic, admin-editable legal page. `slug` decides which document is shown. */
const LegalPage = ({ slug }) => {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  usePageTitle(page?.title || "Policy");

  useEffect(() => {
    let alive = true;
    setPage(null); setError(false);
    axios.get(`${API}/legal/${slug}`)
      .then((r) => { if (alive) setPage(r.data); })
      .catch(() => { if (alive) setError(true); });
    window.scrollTo({ top: 0 });
    return () => { alive = false; };
  }, [slug]);

  const others = LEGAL_LINKS.filter((l) => l.slug !== slug);
  const updated = page?.updated_at ? new Date(page.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : null;

  return (
    <>
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden" data-testid={`legal-hero-${slug}`}>
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-6 h-6" />
          <div className="label-eyebrow mt-4">{page?.eyebrow || "Legal"}</div>
          <h1 className="serif mt-4 text-[40px] sm:text-[54px] lg:text-[64px] leading-[1.05] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }} data-testid="legal-page-title">
            {page?.title || (error ? "Page not found" : "\u00a0")}
          </h1>
          {updated && <p className="mt-5 text-[12px] tracking-[0.18em] uppercase text-[var(--hk-ink-3)]" data-testid="legal-updated">Last updated · {updated}</p>}
        </div>
      </section>

      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <article className="hk-card p-8 lg:p-12" data-testid="legal-page-body">
            {!page && !error && <div className="py-10 text-center text-[var(--hk-ink-3)]"><Loader2 className="animate-spin inline" size={22} /></div>}
            {error && <p className="text-[15px] text-[var(--hk-ink-2)]">This page could not be loaded. Please try again shortly.</p>}
            {page && <ReactMarkdown components={MD_COMPONENTS}>{page.body}</ReactMarkdown>}
          </article>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px]" data-testid="legal-other-links">
            <span className="label-eyebrow">Other policies</span>
            {others.map((l) => (
              <Link key={l.slug} to={l.href} className="inline-flex items-center gap-1 text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)] transition-colors" data-testid={`legal-link-${l.slug}`}>
                {l.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default LegalPage;
